import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Cohort, Level, Profile, Unit } from '@/types'
import {
  ArrowLeft,
  Users,
  Calendar,
  Layers,
  Lock,
  Unlock,
  BookOpen,
  Mail,
  AlertCircle,
  CheckCircle,
  Eye
} from 'lucide-react'

interface CohortWithRelations extends Cohort {
  level: Level | null
}

export default function CohortManage() {
  const { cohortId } = useParams()
  const [cohort, setCohort] = useState<CohortWithRelations | null>(null)
  const [units, setUnits] = useState<Unit[]>([])
  const [students, setStudents] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUnlocking, setIsUnlocking] = useState(false)
  const [unlockSuccess, setUnlockSuccess] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadCohort()
  }, [cohortId])

  const loadCohort = async () => {
    try {
      // Load cohort
      const { data: cohortData, error: cohortError } = await (supabase
        .from('cohorts') as any)
        .select('*')
        .eq('id', cohortId as string)
        .single()

      if (cohortError) throw cohortError

      const cohortRaw = cohortData as Cohort

      // Load level
      const { data: levelData } = await (supabase
        .from('levels') as any)
        .select('*')
        .eq('id', cohortRaw.level_id)
        .single()

      // Load units for this level
      const { data: unitsData } = await supabase
        .from('units')
        .select('*')
        .eq('level_id', cohortRaw.level_id)
        .order('order_num')

      // Load students
      if (cohortRaw.student_ids && cohortRaw.student_ids.length > 0) {
        const { data: studentsData } = await supabase
          .from('profiles')
          .select('*')
          .in('id', cohortRaw.student_ids)

        setStudents((studentsData as unknown as Profile[]) || [])
      }

      setCohort({
        ...cohortRaw,
        level: levelData as Level || null
      } as CohortWithRelations)

      setUnits((unitsData as unknown as Unit[]) || [])
    } catch (error) {
      console.error('Error loading cohort:', error)
      setError('Error al cargar la cohorte')
    } finally {
      setIsLoading(false)
    }
  }

  const unlockNextUnit = async () => {
    if (!cohort) return

    setIsUnlocking(true)
    setError('')
    setUnlockSuccess(false)

    try {
      // Check if there are more units to unlock
      if (cohort.unlocked_unit_index >= units.length - 1) {
        setError('Ya se han desbloqueado todas las unidades')
        return
      }

      // Update the cohort
      const { error } = await (supabase
        .from('cohorts') as any)
        .update({
          unlocked_unit_index: cohort.unlocked_unit_index + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', cohortId as string)

      if (error) throw error

      // Update local state
      setCohort(prev => prev ? {
        ...prev,
        unlocked_unit_index: prev.unlocked_unit_index + 1
      } : null)

      setUnlockSuccess(true)
      setTimeout(() => setUnlockSuccess(false), 3000)
    } catch (error: any) {
      console.error('Error unlocking unit:', error)
      setError(error.message || 'Error al desbloquear la unidad')
    } finally {
      setIsUnlocking(false)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    )
  }

  if (!cohort) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Cohorte no encontrada</p>
        <Link to="/teacher/cohorts" className="text-green-600 hover:underline">
          Volver a mis cohortes
        </Link>
      </div>
    )
  }

  const nextUnitToUnlock = units[cohort.unlocked_unit_index + 1]
  const canUnlockMore = cohort.unlocked_unit_index < units.length - 1

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/teacher/cohorts"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{cohort.name}</h1>
          <p className="text-gray-600">{cohort.description}</p>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Layers className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Nivel</h3>
          </div>
          <p className="text-lg text-gray-800">{cohort.level?.name || 'Sin asignar'}</p>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Estudiantes</h3>
          </div>
          <p className="text-lg text-gray-800">{students.length} / {cohort.max_students}</p>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-green-100">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Inicio</h3>
          </div>
          <p className="text-lg text-gray-800">{formatDate(cohort.start_date)}</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {unlockSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5" />
          <span>Unidad desbloqueada exitosamente. Los estudiantes ahora pueden acceder.</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Units Progress with Unlock Button */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Unidades del Nivel ({cohort.unlocked_unit_index + 1} de {units.length} desbloqueadas)
            </h2>
          </div>

          {canUnlockMore && (
            <button
              onClick={unlockNextUnit}
              disabled={isUnlocking}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Unlock className="w-4 h-4" />
              {isUnlocking ? 'Desbloqueando...' : 'Desbloquear Siguiente Unidad'}
            </button>
          )}
        </div>

        {/* Next Unit Preview */}
        {nextUnitToUnlock && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-700">
              <strong>Proxima unidad a desbloquear:</strong> Unidad {nextUnitToUnlock.order_num}: {nextUnitToUnlock.title}
            </p>
          </div>
        )}

        <div className="space-y-2">
          {units.map((unit, index) => {
            const isUnlocked = index <= cohort.unlocked_unit_index
            const isNext = index === cohort.unlocked_unit_index + 1
            return (
              <div
                key={unit.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  isUnlocked
                    ? 'bg-green-50 border-green-200'
                    : isNext
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className={`p-2 rounded-lg ${
                  isUnlocked ? 'bg-green-100' : isNext ? 'bg-orange-100' : 'bg-gray-100'
                }`}>
                  {isUnlocked ? (
                    <Unlock className="w-4 h-4 text-green-600" />
                  ) : (
                    <Lock className={`w-4 h-4 ${isNext ? 'text-orange-500' : 'text-gray-400'}`} />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${
                    isUnlocked ? 'text-gray-900' : isNext ? 'text-orange-700' : 'text-gray-500'
                  }`}>
                    Unidad {unit.order_num}: {unit.title}
                  </p>
                  <p className="text-xs text-gray-500">{unit.description}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  isUnlocked
                    ? 'bg-green-100 text-green-700'
                    : isNext
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {isUnlocked ? 'Desbloqueada' : isNext ? 'Siguiente' : 'Bloqueada'}
                </span>
              </div>
            )
          })}
        </div>

        {!canUnlockMore && units.length > 0 && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-green-700 font-medium">Todas las unidades han sido desbloqueadas</p>
          </div>
        )}
      </div>

      {/* Students List */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-orange-100">
            <Users className="w-5 h-5 text-orange-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Estudiantes ({students.length})
          </h2>
        </div>

        {students.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-3">
            {students.map((student, index) => (
              <div
                key={student.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 font-medium text-sm">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {student.first_name} {student.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{student.email}</p>
                </div>
                <Link
                  to={`/teacher/students/${student.id}`}
                  className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                  title="Ver progreso"
                >
                  <Eye className="w-4 h-4" />
                </Link>
                <a
                  href={`mailto:${student.email}`}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg">
            No hay estudiantes asignados a esta cohorte
          </div>
        )}
      </div>
    </div>
  )
}
